import React, { useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { AlertTriangle, Search, X } from 'lucide-react';
import { MONTHS_SHORT } from './constants';

export const OverdueDashboardView = ({ data, year }) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchEq, setSearchEq] = useState('');

  if (!data) return <div className="flex-1 flex items-center justify-center text-slate-400">Se incarca...</div>;
  const { stats, equipment, by_section, by_month } = data;

  let filtered = equipment;
  if (activeFilter) {
    if (activeFilter.type === 'stat') {
      if (activeFilter.value === 'pending') filtered = equipment.filter(e => e.pending > 0);
      else if (activeFilter.value === 'complete') filtered = equipment.filter(e => e.pending === 0);
      else if (activeFilter.value === 'overdue_30') filtered = equipment.filter(e => e.max_days_overdue > 30);
      else if (activeFilter.value === 'overdue_month') filtered = equipment.filter(e => e.max_days_overdue > 0 && e.max_days_overdue <= 30);
    } else if (activeFilter.type === 'month') {
      filtered = equipment.filter(e => e.pending_months.includes(activeFilter.value));
    } else if (activeFilter.type === 'section') {
      filtered = equipment.filter(e => e.section === activeFilter.value);
    }
  }
  if (searchEq) {
    const s = searchEq.toLowerCase();
    filtered = filtered.filter(e => e.equipment_name.toLowerCase().includes(s) || e.equipment_code.toLowerCase().includes(s));
  }

  const toggleFilter = (type, value) => {
    if (activeFilter && activeFilter.type === type && activeFilter.value === value) setActiveFilter(null);
    else setActiveFilter({ type, value });
  };

  const isActive = (type, value) => activeFilter && activeFilter.type === type && activeFilter.value === value;

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="overdue-view">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Monitorizare Restante - {year}
        </h2>
        {activeFilter && (
          <button onClick={() => setActiveFilter(null)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <X className="w-3 h-3" /> Sterge filtru
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2.5">
        {[
          { label: 'Total Utilaje', value: stats.total_equipment, color: 'text-slate-800', bg: 'bg-white', filter: null },
          { label: 'Restante', value: equipment.filter(e=>e.pending>0).length, color: 'text-red-600', bg: 'bg-red-50', filter: {type:'stat',value:'pending'} },
          { label: 'La zi', value: stats.equipment_complete, color: 'text-emerald-600', bg: 'bg-emerald-50', filter: {type:'stat',value:'complete'} },
          { label: 'Critice >30z', value: stats.overdue_30, color: 'text-red-700', bg: 'bg-red-50', filter: {type:'stat',value:'overdue_30'} },
          { label: 'Luna anter.', value: stats.overdue_current, color: 'text-amber-600', bg: 'bg-amber-50', filter: {type:'stat',value:'overdue_month'} },
          { label: 'Completare', value: `${stats.completion_pct}%`, color: 'text-blue-600', bg: 'bg-blue-50', filter: null, isPct: true },
        ].map((c, i) => (
          <button key={i} onClick={() => c.filter && toggleFilter(c.filter.type, c.filter.value)}
            className={`rounded-xl border p-3 text-left transition-all ${c.filter ? 'cursor-pointer hover:shadow-md hover:scale-[1.02]' : 'cursor-default'} ${
              isActive(c.filter?.type, c.filter?.value) ? 'ring-2 ring-blue-500 shadow-md scale-[1.02]' : ''
            } ${c.bg}`}>
            <p className="text-[9px] text-slate-500 uppercase font-semibold tracking-wider">{c.label}</p>
            <p className={`text-xl font-bold mt-0.5 ${c.color}`}>{c.value}</p>
            {c.isPct && <div className="h-1.5 bg-slate-200 rounded-full mt-1.5"><div className="h-full bg-blue-500 rounded-full transition-all" style={{width:`${stats.completion_pct}%`}} /></div>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Progress per Month */}
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Progres per Luna</h3>
          <div className="space-y-2">
            {by_month.map(m => {
              const total = m.validated + m.pending;
              const pct = total > 0 ? Math.round(m.validated / total * 100) : 0;
              const active = isActive('month', m.month);
              return (
                <button key={m.month} onClick={() => m.pending > 0 && toggleFilter('month', m.month)}
                  className={`w-full flex items-center gap-3 p-1.5 rounded-lg transition-all ${m.pending > 0 ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'} ${active ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}>
                  <span className="text-xs font-semibold w-10 text-slate-600">{MONTHS_SHORT[m.month - 1]}</span>
                  <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${m.pending === 0 ? 'bg-emerald-400' : 'bg-amber-400'}`} style={{width:`${Math.max(pct, 2)}%`}} />
                  </div>
                  <span className="text-[10px] font-mono w-12 text-right text-slate-500">{m.validated}/{total}</span>
                  <span className={`text-[10px] font-bold w-8 text-right ${m.pending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{pct}%</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Per Section */}
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Per Sectie</h3>
          <div className="space-y-2">
            {by_section.map(s => {
              const total = s.validated + s.pending;
              const pct = total > 0 ? Math.round(s.validated / total * 100) : 0;
              const active = isActive('section', s.section);
              return (
                <button key={s.section} onClick={() => toggleFilter('section', s.section)}
                  className={`w-full flex items-center gap-3 p-1.5 rounded-lg transition-all cursor-pointer hover:bg-slate-50 ${active ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}>
                  <span className="text-xs font-semibold w-20 text-left text-slate-600 truncate">{s.section}</span>
                  <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 rounded-full transition-all" style={{width:`${Math.max(pct, 2)}%`}} />
                  </div>
                  <span className="text-[10px] font-mono w-12 text-right text-slate-500">{s.validated}/{total}</span>
                  <span className={`text-[10px] font-bold w-8 text-right ${s.pending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{pct}%</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Equipment Table */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="flex items-center justify-between p-3 border-b gap-3">
          <h3 className="text-sm font-semibold text-slate-700 flex-shrink-0">
            Utilaje ({filtered.length})
            {activeFilter && <span className="text-[10px] text-blue-500 font-normal ml-2">Filtru activ</span>}
          </h3>
          <div className="relative flex-1 max-w-xs">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="h-7 text-xs pl-8" placeholder="Cauta utilaj..." value={searchEq} onChange={e => setSearchEq(e.target.value)} data-testid="overdue-search" />
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2.5 font-semibold text-slate-600">Utilaj</th>
                <th className="text-left p-2.5 font-semibold text-slate-600">Cod</th>
                <th className="text-left p-2.5 font-semibold text-slate-600">Sectie</th>
                <th className="text-center p-2.5 font-semibold text-slate-600">Progres</th>
                <th className="text-left p-2.5 font-semibold text-slate-600">Luni restante</th>
                <th className="text-right p-2.5 font-semibold text-slate-600">Zile max</th>
                <th className="text-center p-2.5 font-semibold text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(eq => (
                <tr key={eq.equipment_id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-2.5 font-medium text-slate-800">{eq.equipment_name}</td>
                  <td className="p-2.5 text-slate-400 font-mono text-[10px]">{eq.equipment_code}</td>
                  <td className="p-2.5 text-slate-600">{eq.section}</td>
                  <td className="p-2.5">
                    <div className="flex items-center gap-1.5 justify-center">
                      <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${eq.pending === 0 ? 'bg-emerald-400' : eq.completion_pct > 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{width:`${Math.max(eq.completion_pct, 3)}%`}} />
                      </div>
                      <span className="text-[10px] font-mono text-slate-500">{eq.validated}/{eq.total}</span>
                    </div>
                  </td>
                  <td className="p-2.5">
                    <div className="flex gap-1 flex-wrap">
                      {eq.pending_months.map(m => (
                        <span key={m} className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-semibold">{MONTHS_SHORT[m-1]}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-2.5 text-right font-mono font-semibold">
                    <span className={eq.max_days_overdue > 30 ? 'text-red-600' : eq.max_days_overdue > 0 ? 'text-amber-600' : 'text-slate-400'}>
                      {eq.max_days_overdue || '-'}
                    </span>
                  </td>
                  <td className="p-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                      eq.severity === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                      eq.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      eq.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {eq.severity === 'complete' ? 'La zi' : eq.severity === 'critical' ? 'Critic' : eq.severity === 'warning' ? 'Restant' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center py-8 text-slate-400 text-sm">Niciun utilaj gasit</p>}
        </div>
      </div>
    </div>
  );
};
